import { useAuth0 } from "@auth0/auth0-react";
import React, { useState } from "react";
import { PageLayout } from "../components/page-layout";
import { useAuthActions } from "../services/auth.service";
import { SubscriptionStatusCard } from "../components/subscription/SubscriptionStatusCard";
import "../styles/components/grids/profile-grid.css";

export const ProfilePage: React.FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { changePassword, loading } = useAuthActions();
  const [showPasswordInfo, setShowPasswordInfo] = useState(false);
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  if (!user) {
    return null;
  }

  // Only treat the account as unverified when Auth0 says so explicitly; an absent
  // claim (social connections don't always send one) must not trigger the prompt.
  const isEmailUnverified = user.email_verified === false;

  const handleChangePassword = async () => {
    try {
      setPasswordResetError(null);
      setPasswordResetMessage(null);

      await changePassword();
      setPasswordResetMessage("Password reset email sent! Please check your inbox and follow the instructions to reset your password.");
      setShowPasswordInfo(false);
    } catch (error) {
      console.error("Error changing password:", error);

      // Extract error message from the error object
      let errorMessage = "Failed to send password reset email. Please try again.";

      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          errorMessage = "Configuration error: Please contact support. The application needs to be configured to allow direct communication with Auth0.";
        } else if (error.message.includes('Auth0')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }

      setPasswordResetError(errorMessage);
    }
  };

  const dismissPasswordMessage = () => {
    setPasswordResetMessage(null);
    setPasswordResetError(null);
  };

  // `user` is built from the cached ID token, so a freshly verified email only
  // appears after a new token is issued. Bypass the cache, then reload so the
  // whole app re-reads the updated claim.
  const handleRecheckVerification = async () => {
    setCheckingVerification(true);
    setVerificationError(null);

    try {
      await getAccessTokenSilently({ cacheMode: 'off' });
      window.location.reload();
    } catch (error) {
      console.error("Error refreshing verification status:", error);
      setVerificationError(
        "We couldn't confirm your email just yet. Open the link in your inbox, then try again or log out and back in."
      );
      setCheckingVerification(false);
    }
  };

  return (
    <PageLayout className="profile-layout">
      <div className="page-container profile-container">
        <div className="profile-header">
          <h1 className="page-header__title">My account</h1>
          <p className="page-header__subtitle">
            Manage your email, password and subscription plan.
          </p>
        </div>

        {/* Verification prompt - the only place email status is surfaced */}
        {isEmailUnverified && (
          <div className="profile-verify-banner">
            <span className="profile-verify-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </span>
            <div className="profile-verify-content">
              <h2 className="profile-verify-title">Verify your email to use Gymea Edu</h2>
              <p className="profile-verify-text">
                We've sent a verification link to <strong>{user.email}</strong>. Open that link to
                activate your account, then come back here.
              </p>
              {verificationError && (
                <p className="profile-verify-error">{verificationError}</p>
              )}
              <button
                className="profile-button profile-button--primary"
                onClick={handleRecheckVerification}
                disabled={checkingVerification}
              >
                {checkingVerification ? "Checking..." : "I've verified my email"}
              </button>
            </div>
          </div>
        )}

        {/* Password reset success/error messages */}
        {passwordResetMessage && (
          <div className="profile-message profile-message--success">
            <p>{passwordResetMessage}</p>
            <button
              className="profile-message-dismiss"
              onClick={dismissPasswordMessage}
              aria-label="Dismiss message"
            >
              ×
            </button>
          </div>
        )}

        {passwordResetError && (
          <div className="profile-message profile-message--error">
            <p>{passwordResetError}</p>
            <button
              className="profile-message-dismiss"
              onClick={dismissPasswordMessage}
              aria-label="Dismiss message"
            >
              ×
            </button>
          </div>
        )}

        <div className="profile-cards">
          {/* Step 1: the login itself - who you are and how you sign in */}
          <div className="profile-card">
            <div className="profile-card__header">
              <span className="profile-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <h2>Email &amp; password</h2>
            </div>

            <div className="profile-detail">
              <span className="profile-detail__label">Email address</span>
              <p className="profile-detail__value">{user.email}</p>
              <p className="profile-detail__hint">You sign in with this address.</p>
            </div>

            <div className="profile-actions">
              <button
                className="profile-button profile-button--secondary"
                onClick={() => setShowPasswordInfo(true)}
                disabled={loading}
              >
                Change password
              </button>

              {showPasswordInfo && (
                <div className="profile-info-box">
                  <p className="profile-info-text">
                    We'll send a password reset email to <strong>{user.email}</strong>.
                    You can then follow the instructions in the email to reset your password.
                  </p>
                  <div className="profile-info-actions">
                    <button
                      className="profile-button profile-button--primary"
                      onClick={handleChangePassword}
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send reset email"}
                    </button>
                    <button
                      className="profile-button profile-button--secondary"
                      onClick={() => setShowPasswordInfo(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Step 2: what that login gets you */}
          <div className="profile-card">
            <div className="profile-card__header">
              <span className="profile-card__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </span>
              <h2>Subscription plan</h2>
            </div>

            {/* The card owns the whole plan story: which plan, its status,
                and how to change it. */}
            <SubscriptionStatusCard />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
